import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Drawer, Stepper, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { defineStepper } from '@stepperize/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import z from 'zod'

import {
  NewsContentForm,
  NewsContentSchema,
} from '../../schemas/NewsContentSchema'
import { NewsCropForm, NewsCropSchema } from '../../schemas/NewsImageSchema'
import { NewsOrderForm } from '../../schemas/NewsOrderSchema'
import { NewsForm, NewsSchema } from '../../schemas/NewsSchema'
import {
  NewsSummaryForm,
  NewsSummarySchema,
} from '../../schemas/NewsSummarySchema'
import { useCreateNews } from '../hooks/useCreateNews'
import { FormNewsContent } from './FormNewsContent'
import { FormNewsImage } from './FormNewsImage'
import { FormNewsSummary } from './FormNewsSummary'
import { NewsPreview } from './NewsPreview'
import { ToastNewsMissingFields } from './ToastNewsMissingFields'

type Form = NewsSummaryForm &
  NewsContentForm &
  NewsCropForm &
  NewsOrderForm &
  NewsForm

export function DrawerCreateNews() {
  const { useStepper, utils } = defineStepper(
    {
      id: 'summaryNews',
      schema: NewsSummarySchema(),
    },
    {
      id: 'contentNews',
      schema: NewsContentSchema(),
    },
    {
      id: 'cropNews',
      schema: NewsCropSchema(),
    },
    {
      id: 'previewNews',
      schema: z.object({}),
    },
  )

  const { t } = useTranslation('panel')
  const stepper = useStepper()
  const drawerCreateNews = useDrawer('drawer-create-news')
  const hookForm = useForm({
    resolver: zodResolver(stepper.current.schema),
  })
  const { handleCreateNews, isCreatingNews } = useCreateNews()

  const handleSubmit = () => {
    const data = hookForm.getValues() as Form

    if (stepper.isLast) {
      const { success } = NewsSchema.safeParse(data)

      if (!success) {
        return toast(ToastNewsMissingFields, {
          autoClose: 8000,
          data: {
            onLinkOrFile: () => stepper.goTo('summaryNews'),
            onContent: () => stepper.goTo('contentNews'),
          },
        })
      }

      return handleCreateNews(data)
    }

    if (!data?.image && stepper.current.id === 'contentNews') {
      return stepper.goTo('previewNews')
    }

    return stepper.next()
  }

  const handleBack = () => {
    const data = hookForm.getValues() as Form

    if (!data?.image && stepper.current.id === 'previewNews') {
      return stepper.goTo('contentNews')
    }

    stepper.prev()
  }

  const handleClose = () => {
    drawerCreateNews.close()
    hookForm.reset({
      title: '',
      tag: [],
      subtitle: '',
      postExternalUrl: '',
      postFile: undefined,
      content: '',
      image: undefined,
      postImage: undefined,
      sliderSorte: 0,
    })
    stepper.reset()
  }

  const CURRENT_STEP = utils.getIndex(stepper.current.id) + 1

  return (
    <Drawer.Root id="drawer-create-news">
      <Drawer.Header className="w-full flex gap-4" onClose={handleClose}>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('news.drawer_create_news.header.title')}
        </h1>

        <Stepper.Root className="sm:min-w-sm mx-auto">
          <Stepper.StepNumber
            number={1}
            isCompleted={CURRENT_STEP > 1}
            isActive={stepper.current.id === 'summaryNews'}
          />

          <Stepper.StepNumber
            number={2}
            isCompleted={CURRENT_STEP > 2}
            isActive={stepper.current.id === 'contentNews'}
          />

          <Stepper.StepNumber
            number={3}
            isCompleted={CURRENT_STEP > 3}
            isActive={stepper.current.id === 'cropNews'}
          />

          <Stepper.StepNumber
            number={4}
            isActive={stepper.current.id === 'previewNews'}
          />
        </Stepper.Root>
      </Drawer.Header>

      <Drawer.Body>
        <FormProvider {...hookForm}>
          <form
            onSubmit={hookForm.handleSubmit(handleSubmit)}
            className="h-full"
          >
            {stepper.switch({
              summaryNews: () => <FormNewsSummary />,
              cropNews: () => <FormNewsImage aspectRatio={3.5 / 1} />,
              contentNews: () => (
                <FormNewsContent
                  aspectRatio="3.5 / 1"
                  sizes={{
                    ideal: '1200 × 200',
                    firstOption: '900 × 150',
                    secondOption: '1800 × 300',
                  }}
                />
              ),
              previewNews: () => <NewsPreview />,
            })}
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer className="flex gap-4 justify-end">
        {stepper.isFirst ? (
          <Button.Root variant="secondary" onClick={handleClose}>
            {t('news.drawer_create_news.footer.button_cancel')}
          </Button.Root>
        ) : (
          <Button.Root variant="secondary" onClick={handleBack}>
            {t('news.drawer_create_news.footer.button_prev')}
          </Button.Root>
        )}

        <Button.Root
          onClick={hookForm.handleSubmit(handleSubmit)}
          isLoading={isCreatingNews}
        >
          {stepper.isLast
            ? t('news.drawer_create_news.footer.button_submit')
            : t('news.drawer_create_news.footer.button_next')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
